import { useState, useEffect, useRef, useCallback } from 'react';
import { Stack, Group, Text, Box } from '@mantine/core';
import { Lock, Check, AlertTriangle } from 'react-feather';
import { RichTextEditor } from '../../../components/common/RichTextEditor';
import { coachPortalApi } from '../../../services/api/coachPortal';

interface MyNotesPanelProps {
  sessionId: string;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export function MyNotesPanel({ sessionId }: MyNotesPanelProps) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [displayTime, setDisplayTime] = useState('');

  // Refs for debounce/flush
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingContentRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);

  // Fetch existing notes on mount
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const result = await coachPortalApi.getSessionNotes(sessionId);
        if (result.content) {
          setContent(result.content);
        }
        if (result.updated_at) {
          setLastSavedAt(new Date(result.updated_at));
        }
      } catch (error) {
        console.error('Failed to load notes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [sessionId]);

  // Update relative time display
  useEffect(() => {
    if (!lastSavedAt) return;
    setDisplayTime(formatRelativeTime(lastSavedAt));
    const interval = setInterval(() => {
      setDisplayTime(formatRelativeTime(lastSavedAt));
    }, 30000);
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  // Save function
  const saveNotes = useCallback(
    async (noteContent: string) => {
      setSaveStatus('saving');
      try {
        const result = await coachPortalApi.upsertSessionNotes(sessionId, noteContent);
        setSaveStatus('saved');
        setLastSavedAt(new Date(result.updated_at));
        retryCountRef.current = 0;
        pendingContentRef.current = null;
      } catch (error) {
        console.error('Failed to save notes:', error);
        setSaveStatus('error');
        // Auto-retry up to 3 times
        if (retryCountRef.current < 3) {
          retryCountRef.current++;
          debounceTimerRef.current = setTimeout(() => {
            saveNotes(noteContent);
          }, 2000 * retryCountRef.current);
        }
      }
    },
    [sessionId],
  );

  // Debounced save on content change
  const handleContentChange = useCallback(
    (newContent: string) => {
      setContent(newContent);
      pendingContentRef.current = newContent;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        saveNotes(newContent);
      }, 1500);
    },
    [saveNotes],
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Fire-and-forget save if there's pending content
      if (pendingContentRef.current !== null) {
        coachPortalApi
          .upsertSessionNotes(sessionId, pendingContentRef.current)
          .catch(() => {});
      }
    };
  }, [sessionId]);

  // Save status indicator text
  const statusIndicator = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <Text size="xs" c="dimmed">
            Saving...
          </Text>
        );
      case 'saved':
        return (
          <Group gap={4}>
            <Check size={12} color="var(--medley-green-success)" />
            <Text size="xs" c="dimmed">
              Saved {displayTime}
            </Text>
          </Group>
        );
      case 'error':
        return (
          <Group gap={4}>
            <AlertTriangle size={12} color="var(--medley-red-danger)" />
            <Text size="xs" c="red">
              Save failed — retrying...
            </Text>
          </Group>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Text size="sm" c="dimmed">
          Loading notes...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm" style={{ height: '100%' }}>
      {/* Privacy indicator */}
      <Group gap={6}>
        <Lock size={14} color="var(--medley-text-tertiary)" />
        <Text size="xs" c="dimmed">
          Only visible to you
        </Text>
      </Group>

      {/* Editor — fills available space */}
      <Box style={{ flex: 1, minHeight: 300 }}>
        <RichTextEditor
          value={content}
          onChange={handleContentChange}
          placeholder="Add your personal notes for this session..."
          minHeight={400}
        />
      </Box>

      {/* Save status indicator */}
      <Group justify="flex-end">{statusIndicator()}</Group>
    </Stack>
  );
}

export default MyNotesPanel;
